import { useState } from "react";
import { BookText, ChevronDown, Trash2 } from "lucide-react";

import { AddNewEntry } from "@/components/Assistant/AssistantDetailsContent";
import { GuidelinesField } from "@/components/Assistant/GuidelinesField";
import { ToolSectionHeader } from "@/components/Assistant/ToolSectionHeader";
import type { NoxusFormEntryConfig } from "@/components/Spotflow/ConfigDrawer/ConfigDrawer";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/shadcn/collapsible";
import { NoxusMenu } from "@/components/UI/NoxusMenu";
import { cn } from "@/lib/utils";

export const GuidelinesFieldWithHeader = ({
  form,
  idFieldKey,
  config,
  isDisabled,
}: {
  form: ReturnType<typeof import("react-hook-form").useFormContext>;
  idFieldKey: string;
  config: NoxusFormEntryConfig;
  isDisabled?: boolean;
}) => {
  const enabledKey = idFieldKey.replace(/\.guidelines$/, ".guidelines_enabled");
  const toolsFieldKey = idFieldKey.replace(/guidelines$/, "tools");
  const isEnabled = form.watch(enabledKey) ?? false;

  return (
    <div className="flex flex-col gap-3">
      <ToolSectionHeader
        title="Guidelines"
        subtitle="Behavioral rules that shape how the agent responds"
        enabled={isEnabled}
        onToggle={(checked) =>
          form.setValue(enabledKey, checked, { shouldDirty: true })
        }
        isDisabled={isDisabled}
      />

      {isEnabled && (
        <GuidelinesField
          guidelines={form.watch(idFieldKey) ?? config.default}
          availableTools={form.watch(toolsFieldKey) ?? []}
          availableMcpServers={[]}
          onGuidelinesChange={(value) =>
            form.setValue(idFieldKey, value, { shouldDirty: true })
          }
          isDisabled={isDisabled}
        />
      )}
    </div>
  );
};

export const GlossaryFieldStandalone = ({
  form,
  idFieldKey,
  config,
  isDisabled,
}: {
  form: ReturnType<typeof import("react-hook-form").useFormContext>;
  idFieldKey: string;
  config: NoxusFormEntryConfig;
  isDisabled?: boolean;
}) => {
  const terms = (form.watch(idFieldKey) ?? config.default ?? []) as Array<{
    term: string;
    definition: string;
    synonyms?: string[];
  }>;

  const updateTerm = (
    index: number,
    patch: Partial<(typeof terms)[number]>
  ) => {
    if (isDisabled) return;
    const updated = terms.map((t, i) => (i === index ? { ...t, ...patch } : t));
    form.setValue(idFieldKey, updated, { shouldDirty: true });
  };

  const deleteTerm = (index: number) => {
    if (isDisabled) return;
    form.setValue(
      idFieldKey,
      terms.filter((_, i) => i !== index),
      { shouldDirty: true }
    );
  };

  const addTerm = () => {
    if (isDisabled) return;
    form.setValue(
      idFieldKey,
      [...terms, { term: "", definition: "", synonyms: [] }],
      { shouldDirty: true }
    );
  };

  return (
    <div className="flex flex-col gap-3">
      <ToolSectionHeader
        title="Glossary"
        subtitle="Define terms that help the agent interpret domain-specific vocabulary"
      />

      <div className="flex flex-col gap-2">
        {terms.map((term, index) => (
          <GlossaryTermCard
            key={index}
            term={term}
            onChange={(patch) => updateTerm(index, patch)}
            onDelete={() => deleteTerm(index)}
            isDisabled={isDisabled}
          />
        ))}

        <AddNewEntry
          label="Add Term"
          onClick={addTerm}
          isDisabled={isDisabled}
        />
      </div>
    </div>
  );
};

const GlossaryTermCard = ({
  term,
  onChange,
  onDelete,
  isDisabled,
}: {
  term: { term: string; definition: string; synonyms?: string[] };
  onChange: (patch: Partial<typeof term>) => void;
  onDelete: () => void;
  isDisabled?: boolean;
}) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <div className="border border-border rounded-md w-full overflow-hidden">
        <div className="flex items-center justify-between gap-2 px-2 py-1.5">
          <CollapsibleTrigger asChild>
            <button
              type="button"
              className="flex items-center gap-2 flex-1 min-w-0 cursor-pointer"
            >
              <div className="flex bg-accent text-muted-foreground p-1.5 rounded-md justify-center items-center">
                <BookText width={16} height={16} />
              </div>
              <p className="text-sm truncate min-w-0 text-left">
                {term.term || "New Term"}
              </p>
              {term.definition && !isOpen && (
                <span className="text-xs text-muted-foreground truncate">
                  - {term.definition}
                </span>
              )}
              <ChevronDown
                className={cn(
                  "h-4 w-4 text-muted-foreground shrink-0 transition-transform",
                  isOpen && "rotate-180"
                )}
              />
            </button>
          </CollapsibleTrigger>
          <NoxusMenu
            isSmall
            itemsArray={[
              {
                label: "Delete",
                icon: Trash2,
                onClick: onDelete,
                color: "destructive",
              },
            ]}
            isDisabled={isDisabled}
          />
        </div>

        <CollapsibleContent>
          <div className="border-t border-border p-3 flex flex-col gap-2.5">
            <div className="flex flex-col gap-1">
              <label className="text-xs text-muted-foreground">Term</label>
              <input
                type="text"
                className="w-full px-2.5 py-1.5 text-sm border border-input rounded-md bg-background placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring"
                placeholder="e.g. NRR"
                value={term.term}
                onChange={(e) => onChange({ term: e.target.value })}
                disabled={isDisabled}
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs text-muted-foreground">
                Definition
              </label>
              <input
                type="text"
                className="w-full px-2.5 py-1.5 text-sm border border-input rounded-md bg-background placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring"
                placeholder="Net Revenue Retention"
                value={term.definition}
                onChange={(e) => onChange({ definition: e.target.value })}
                disabled={isDisabled}
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs text-muted-foreground">
                Synonyms (comma-separated)
              </label>
              <input
                type="text"
                className="w-full px-2.5 py-1.5 text-sm border border-input rounded-md bg-background placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring"
                placeholder="e.g. Net Retention, Net Dollar Retention"
                value={(term.synonyms || []).join(", ")}
                onChange={(e) =>
                  onChange({
                    synonyms: e.target.value
                      .split(",")
                      .map((s) => s.trim())
                      .filter(Boolean),
                  })
                }
                disabled={isDisabled}
              />
            </div>
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
};
